/**
 * Synchronous consent-transition ledger (browser memory only).
 *
 * Every consent write is recorded here *before* the CONSENT_EVENT is
 * dispatched, so delayed dynamic-import handlers can never miss a
 * withdrawal that was followed by a quick re-grant. Each transition gets a
 * monotonically increasing generation; local state captured at generation G
 * is revivable only if no ads withdrawal happened after G.
 *
 * Scope: locally-owned, still-unsent state only (memory, our localStorage,
 * the not-yet-flushed fbq stub queue). Anything already sent to a platform
 * cannot be recalled from here.
 */

/** Our own advertising storage keys that a withdrawal must clear synchronously. */
export const ADS_WITHDRAWAL_STORAGE_KEYS: readonly string[] = ["lecmo:meta_click", "lecmo:snap_click"];

let generation = 0;
let lastAdsWithdrawalGen = 0;
const withdrawalHooks = new Set<() => void>();

export interface ConsentTransition {
  generation: number;
  ads: boolean;
  analytics: boolean;
}

export function currentConsentGeneration(): number {
  return generation;
}

/** True when an ads withdrawal was recorded after `capturedGen`. */
export function adsWithdrawnSince(capturedGen: number): boolean {
  return lastAdsWithdrawalGen > capturedGen;
}

/** Registers a synchronous hook run on every ads withdrawal. */
export function onAdsWithdrawn(fn: () => void): () => void {
  withdrawalHooks.add(fn);
  return () => withdrawalHooks.delete(fn);
}

/** Record a transition synchronously. Called by writeConsent before dispatch. */
export function noteConsentTransition(next: { ads: boolean; analytics: boolean }): ConsentTransition {
  generation += 1;
  if (!next.ads) {
    lastAdsWithdrawalGen = generation;
    if (typeof window !== "undefined") {
      for (const key of ADS_WITHDRAWAL_STORAGE_KEYS) {
        try {
          window.localStorage?.removeItem(key);
        } catch {
          /* storage blocked */
        }
      }
    }
    for (const hook of withdrawalHooks) {
      try {
        hook();
      } catch {
        /* a hook must not block other hooks */
      }
    }
  }
  return { generation, ads: next.ads, analytics: next.analytics };
}

/** Test-only reset. */
export function __resetConsentTransitionsForTests(): void {
  generation = 0;
  lastAdsWithdrawalGen = 0;
  withdrawalHooks.clear();
}
